import { AlertService } from '../../../../services/alert.service';
import { useLocation, useNavigate } from 'react-router-dom';
import { Header } from 'rsuite';
import React from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { InputWrapper } from '../../../common/input-wrapper';
import {
  decimalValidation,
  intValidation,
  required,
} from '../../../../utils/validations';
import { getCurrencySymbol } from '../../../../utils/common.utils';
import { AddButton } from '../../../common/add-button';
import useSWRMutation from 'swr/mutation';
import { trpc, RouterInput } from '../../../../api/trpc';
import { CleanCustomsDeliverySupport } from '../../../../export-types/cleaned-types';

export type Form = {
  name: string;
  period: number;
  disabled: boolean;
  threshold: number;
  tariffs: Array<{
    commissionRate: string;
    price: string;
  }>;
};

export const CustomsSupportCreateScreen: React.FC = () => {
  const navigate = useNavigate();

  const { trigger: create } = useSWRMutation(
    ['customsDeliverySupport.create'],
    (_k, { arg }: { arg: RouterInput['customsDeliverySupport']['create'] }) =>
      trpc.customsDeliverySupport.create.mutate(arg),
  );

  const { trigger: update } = useSWRMutation(
    ['customsDeliverySupport.update'],
    (_k, { arg }: { arg: RouterInput['customsDeliverySupport']['update'] }) =>
      trpc.customsDeliverySupport.update.mutate(arg),
  );
  const { state } = useLocation() as {
    state?: CleanCustomsDeliverySupport;
  };
  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Form>({
    defaultValues: {
      ...state,
      disabled: state?.disabled ?? true,
      tariffs: state?.tariffs.map((tariff) => {
        return {
          price: tariff.price,
          commissionRate: tariff.commissionRate.toString(),
        };
      }) || [{}],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'tariffs',
  });

  const onSubmit = React.useMemo(
    () =>
      handleSubmit(async (data) => {
        const processedData = {
          ...data,
          threshold: +data.threshold,
          period: +data.period,
          tariffs: data.tariffs.map((tariff) => ({
            commissionRate: +tariff.commissionRate,
            price: +tariff.price,
          })),
        };
        if (state?.id) {
          await update({
            id: state.id,
            data: processedData,
          });
        } else {
          await create(processedData);
        }
        AlertService.success();
        navigate(-1);
      }),
    [create, handleSubmit, navigate, state?.id, update],
  );

  return (
    <>
      <Header />
      <div className="container">
        <h1 className="mb-4">Добавить сопровождение поставки</h1>
        <form onSubmit={onSubmit}>
          <div className="row mb-3">
            <InputWrapper
              title="Название"
              error={errors.name?.message}
              className="col-9"
            >
              <input
                className="form-control"
                {...register('name', {
                  ...required,
                })}
              />
            </InputWrapper>
            <InputWrapper
              title="Период расчёта"
              error={errors.period?.message}
              className="col-3"
            >
              <div className="input-group">
                <input
                  className="form-control"
                  type="text"
                  {...register('period', {
                    ...required,
                    ...intValidation,
                    min: {
                      value: 1,
                      message: 'Минимум 1 день',
                    },
                  })}
                />
                <span className="input-group-text">дней</span>
              </div>
            </InputWrapper>
            <InputWrapper
              title="Минимальное значение комиссии"
              error={errors.threshold?.message}
              className="col-3"
            >
              <div className="input-group">
                <input
                  className="form-control"
                  type="text"
                  {...register('threshold', {
                    ...required,
                    ...intValidation,
                  })}
                />
                <span className="input-group-text">
                  {getCurrencySymbol('rub')}
                </span>
              </div>
            </InputWrapper>
          </div>
          <div className="row">
            <div className="col">
              <h3>Тарифная сетка</h3>
            </div>
          </div>
          <div className="row">
            <InputWrapper
              className="col-6"
              title="Стоимость поставки, до"
              required
            />
            <InputWrapper className="col" title="Размер комиссии" required />
          </div>
          {fields.map((_, index) => (
            <div className="row mb-3" key={index}>
              <InputWrapper
                className="col-6"
                error={errors.tariffs?.[index]?.price?.message}
              >
                <div className="input-group">
                  <input
                    className="form-control"
                    type="text"
                    {...register(`tariffs.${index}.price`, {
                      ...required,
                      ...decimalValidation,
                    })}
                  />
                  <span className="input-group-text">
                    {getCurrencySymbol('rub')}
                  </span>
                </div>
              </InputWrapper>
              <InputWrapper
                className="col"
                error={errors.tariffs?.[index]?.commissionRate?.message}
              >
                <div className="input-group">
                  <input
                    className="form-control"
                    type="text"
                    {...register(`tariffs.${index}.commissionRate`, {
                      ...required,
                      ...decimalValidation,
                    })}
                  />
                  <span className="input-group-text">%</span>
                </div>
              </InputWrapper>
              <div className="col col-auto">
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={() => remove(index)}
                  disabled={fields.length === 1}
                >
                  <i className="bi bi-trash p-2 cursor-pointer" />
                </button>
              </div>
            </div>
          ))}
          <div className="row mb-3">
            <div className="col-12">
              <AddButton
                onClick={() => append({ price: '', commissionRate: '' })}
              >
                Добавить тариф
              </AddButton>
            </div>
          </div>
          <div className="row mb-4">
            <div className="col-12">
              <button
                type="submit"
                className="btn btn-success w-100"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  />
                ) : (
                  'Сохранить'
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};
